/**
 * useURLFilters
 *
 * A standalone hook that provides bidirectional synchronisation between
 * an arbitrary filter state object and URL search parameters.
 *
 * Features:
 *   - Reads initial filter values from the current URL on mount
 *   - Writes filter values back to the URL on change (debounced)
 *   - Supports string, string-array, and ISO-date serialisation
 *   - Preserves non-filter URL params that may already be present
 *   - Uses `replace` navigation to avoid polluting browser history
 *
 * Usage:
 *   const { params, setParam, clearParam, clearAll } = useURLFilters({
 *     paramKeys: ['country', 'brand', 'retailer', 'category', 'store', 'from', 'to'],
 *     debounceMs: 300,
 *   });
 *
 * Note: The global FilterContext already integrates URL sync internally.
 * This hook is provided for advanced use-cases where a component needs its
 * own isolated set of URL-synced parameters (e.g. a page-specific filter
 * that should not clash with the global filters).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// ---- Types ----

export type ParamValue = string | string[] | Date | null;

export interface URLFilterParams {
  [key: string]: ParamValue;
}

export interface UseURLFiltersOptions {
  /** The URL param keys this hook manages. Others are left untouched. */
  paramKeys: string[];
  /** Debounce delay (ms) before writing changes to the URL. Default 300. */
  debounceMs?: number;
}

export interface UseURLFiltersReturn {
  /** Current param values (deserialized from URL). */
  params: URLFilterParams;
  /** Set a single param value. Pass null to remove it. */
  setParam: (key: string, value: ParamValue) => void;
  /** Remove a single param from the URL. */
  clearParam: (key: string) => void;
  /** Remove all managed params from the URL. */
  clearAll: () => void;
  /** Build a query-string fragment from the current params (useful for API calls). */
  toQueryString: () => string;
}

// ---- Serialisation helpers ----

function serialize(value: ParamValue): string | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(',') : null;
  }
  return value || null;
}

function deserialize(raw: string | null): ParamValue {
  if (raw === null || raw === '') return null;
  // Try to detect ISO date (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? raw : d;
  }
  // Comma-separated -> array
  if (raw.includes(',')) {
    return raw.split(',').filter(Boolean);
  }
  return raw;
}

// ---- Hook ----

export function useURLFilters(options: UseURLFiltersOptions): UseURLFiltersReturn {
  const { paramKeys, debounceMs = 300 } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const managedKeys = useRef(new Set(paramKeys));

  // Keep managedKeys in sync if paramKeys changes
  useEffect(() => {
    managedKeys.current = new Set(paramKeys);
  }, [paramKeys]);

  // Read initial state from URL
  const [params, setParams] = useState<URLFilterParams>(() => {
    const initial: URLFilterParams = {};
    paramKeys.forEach((key) => {
      initial[key] = deserialize(searchParams.get(key));
    });
    return initial;
  });

  // Sync params -> URL (debounced)
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const current = new URLSearchParams(window.location.search);

      // Remove all managed keys first
      managedKeys.current.forEach((key) => current.delete(key));

      // Add current param values
      Object.entries(params).forEach(([key, value]) => {
        if (!managedKeys.current.has(key)) return;
        const serialized = serialize(value);
        if (serialized !== null) {
          current.set(key, serialized);
        }
      });

      setSearchParams(current, { replace: true });
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, debounceMs]);

  // --- Actions ---

  const setParam = useCallback((key: string, value: ParamValue) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearParam = useCallback((key: string) => {
    setParams((prev) => ({ ...prev, [key]: null }));
  }, []);

  const clearAll = useCallback(() => {
    setParams((prev) => {
      const cleared: URLFilterParams = {};
      Object.keys(prev).forEach((key) => {
        cleared[key] = null;
      });
      return cleared;
    });
  }, []);

  const toQueryString = useCallback((): string => {
    const parts: string[] = [];
    Object.entries(params).forEach(([key, value]) => {
      const s = serialize(value);
      if (s !== null) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(s)}`);
      }
    });
    return parts.join('&');
  }, [params]);

  return { params, setParam, clearParam, clearAll, toQueryString };
}

export default useURLFilters;
